import React from 'react';
import Dialog from 'material-ui/Dialog';
import Button from '@material-ui/core/Button';
import FileUploadCallDetailsActions from 'actions/files/FileUploadCallDetailsActions';
import CallSyncCRMContactLeadSearchInputBoxContainer from 'components/stateful/call-card/sync-crm/CallSyncCRMContactLeadSearchInputBoxContainer';
import CallSyncCRMRelatedToSearchInputBoxContainer from 'components/stateful/call-card/sync-crm/CallSyncCRMRelatedToSearchInputBoxContainer';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import CloseIcon from 'images/common/CloseModal.svg';
import MessageBox from 'components/pure/utils/MessageBox';
import 'styles/lib/forms.styl';
import 'styles/call-card/sync-crm/CustomReactSelectize.styl';
import {ThemeProvider} from '@components/@theme';
import {lightTheme} from '@components/@theme/lightTheme';

/**
 * Enumerate all the possible data fulfill types in the CRMSyncDialog's search input boxes, some of them are valid ones
 * while the others are not for submission to the backend
 * @type {{CONTACT_AND_ACCOUNT: string}}
 */
const CallSyncCRMDataFulfillType = {
  CONTACT_AND_ACCOUNT: 'CONTACT_AND_ACCOUNT',
  LEAD_ONLY: 'LEAD_ONLY',
  ACCOUNT_OR_OPPORTUNITY_ONLY: 'ACCOUNT_OR_OPPORTUNITY_ONLY',
  MISSING_RELATED_TO: 'MISSING_RELATED_TO',
};

/**
 * <CallSyncCRMDialog /> renders a modal material-ui dialog to allow the caller / manager of a call to update its
 * metadata in both the salesforce CRM and the elasticsearch cluster
 */
class CallSyncCRMDialog extends React.Component {
  constructor() {
    super();
    this.state = {
      renderDataFulfillTypeMessageBox: false,
    };
    this.onTouchTapCloseButton = this.onTouchTapCloseButton.bind(this);
    this.onTouchTapSaveButton = this.onTouchTapSaveButton.bind(this);
  }

  /** Close the CallSyncCRMDialog by informing FileUploadCallDetailsStore to set its related state variable to null */
  onTouchTapCloseButton() {
    FileUploadCallDetailsActions.hideCallSyncCRM();
    this.setState({renderDataFulfillTypeMessageBox: false});
  }

  /**
   * Save the user-selected contacts / leads / opportunities into elasticsearch documents' contacts / companies on the
   * backend using an endpoint provided for crm-data-sync
   */
  onTouchTapSaveButton() {
    if (this.dialogDataFulfillType() === CallSyncCRMDataFulfillType.MISSING_RELATED_TO) {
      this.setState({renderDataFulfillTypeMessageBox: true});
    } else {
      const contact = this.contactFromProps(this.props);
      const company = this.companyFromProps(this.props);
      const objectId = this.objectIdFromProps(this.props);
      CrmSyncActions.syncCrmData(this.props.fileUploadId, contact, company, objectId);
      this.setState({renderDataFulfillTypeMessageBox: false});
    }
  }

  /**
   * Return the salesforce object id which will be used by the backend to populate the crm_link field value for the
   * file's elasticsearch document. If either contact or lead is chosen, then return contact / lead's id. If neither
   * contact nor lead is chosen, then return the chosen account / opportunity's id
   * @param {Object} props Parent AltContainer component's props
   * @return {String} id Salesforce object id for contact / lead / account / opportunity
   */
  objectIdFromProps(props) {
    const item = props.contactsAndLeads.first();
    if (item) {
      return item.option.id;
    } else {
      const item = props.accountsAndOpportunities.first();
      if (item) {
        return item.option.id;
      }
    }
    // Should never hit this line
    return null;
  }

  /**
   * Return the contact value chosen by the user. This value could come from either 'Contact.Name' or 'Lead.Name',
   * depending on whichever one is available
   * @param {Object} props Parent AltContainer component's props
   * @return {String / null} contact Contact value to be passed to the backend for crm data sync
   */
  contactFromProps(props) {
    let contact;
    const contactOrLead = this.firstContactOrLeadValue(props);
    if (contactOrLead && contactOrLead.option) {
      contact = this.contactFromOption(contactOrLead.option);
    }
    return contact;
  }

  /**
   * return the company value chosen by the user. This value could come from either 'Contact.Account.Name',
   * 'Lead.Company' or 'Opportunity.Account.Name'
   * @param {Object} props Parent AltContainer component's props
   * @return {String / null} company Company value to be passed to the backend for crm data sync
   */
  companyFromProps(props) {
    let company;
    const accountsAndOpportunitiesValues = props.accountsAndOpportunities;
    if (accountsAndOpportunitiesValues.count() > 0) {
      const item = accountsAndOpportunitiesValues.first();
      if (item && item.option) {
        company = this.companyFromOption(item.option);
      }
    }
    if (!company) {
      const contactOrLead = this.firstContactOrLeadValue(props);
      if (contactOrLead && contactOrLead.option && contactOrLead.option.type === 'lead') {
        company = contactOrLead.option.company;
      }
    }
    return company;
  }

  /**
   * Return the first contact / lead value
   */
  firstContactOrLeadValue(props) {
    const contactsAndLeadsValues = props.contactsAndLeads;
    return contactsAndLeadsValues.first();
  }

  /**
   * Return the company value in a given option. If `option.type === 'account'`, then the value is `option.name`. If
   * `option.type === 'lead'`, then the value is `option.company`. If `option.type === 'opportunity`, then the value is
   * `option.account.name`
   * @param {Object} option Parent AltContainer component's props
   * @return {String / null} company Company value to be passed to the backend for crm data sync
   */
  companyFromOption(option) {
    if (option.type === 'account') {
      return option.name;
    } else if (option.type === 'lead') {
      return option.company;
    } else if (option.type === 'opportunity') {
      return option.account.name;
    }
    return null;
  }

  /**
   * Return the contact value in a given option. If `option.type === 'contact'`, then the value is `option.name`. If
   * `option.type === 'lead'`, then the value is `option.name` as well
   * @param {Object} option Option chosen by the user that is either a contact or a lead
   */
  contactFromOption(option) {
    if (option.type === 'contact' || option.type === 'lead') {
      return option.name;
    }
    return null;
  }

  /** Return true to mark the save button disabled when none of accounts/opportunities/contacts/leads are chosen by the
   *  user. If either one of these is chosen, then return false
   */
  dialogDataFulfillType() {
    const accountsAndOpportunitiesValues = this.props.accountsAndOpportunities;
    const contactsAndLeadsValues = this.props.contactsAndLeads;
    if (contactsAndLeadsValues && contactsAndLeadsValues.count() > 0) {
      if (contactsAndLeadsValues.first().option.type === 'contact') {
        if (accountsAndOpportunitiesValues && accountsAndOpportunitiesValues.count() > 0) {
          // User chose contact + account -> allow to save
          return CallSyncCRMDataFulfillType.CONTACT_AND_ACCOUNT;
        }
      } else {
        // User chose lead -> allow to save
        return CallSyncCRMDataFulfillType.LEAD_ONLY;
      }
    } else if (accountsAndOpportunitiesValues && accountsAndOpportunitiesValues.count() > 0) {
      // User chose account / opportunity -> allow to save
      return CallSyncCRMDataFulfillType.ACCOUNT_OR_OPPORTUNITY_ONLY;
    }
    // User didn't choose related-to, not allowed to save
    return CallSyncCRMDataFulfillType.MISSING_RELATED_TO;
  }

  /** Return a <MessageBox/> component whenever only an opportunity is chosen by the user */
  renderContactLeadTipLine() {
    const contactAndLead = this.props.contactsAndLeads.first();
    const accountOrOpportunity = this.props.accountsAndOpportunities.first();
    if (
      accountOrOpportunity &&
      accountOrOpportunity.option.type === 'opportunity' &&
      !contactAndLead
    ) {
      return (
        <MessageBox
          style={{marginBottom: '20px'}}
          message={`Tip: If you have this contact saved in the account that this opportunity belongs to, you can select his/her name in the ‘Contact:' field.`}
        />
      );
    }
  }

  /** Return a <MessageBox/> component whenever the user mis-selects all the necessary data for sync */
  renderMissingFulfillDataTipLine() {
    const dataFulfillType = this.dialogDataFulfillType();
    if (
      dataFulfillType === CallSyncCRMDataFulfillType.MISSING_RELATED_TO &&
      this.state.renderDataFulfillTypeMessageBox
    ) {
      return (
        <MessageBox
          style={{
            marginBottom: '20px',
            backgroundColor: '#F0C141',
          }}
          message={`Please select an account or opportunity from the Related To dropdown.`}
        />
      );
    }
  }

  render() {
    // Move the dialog element closer to the top of the window so that its dropdowns are not cut off by the edge of the
    // browser window
    const dialogTopOffset = '-100px';
    return (
      <Dialog
        open={this.props.open}
        autoScrollBodyContent={true}
        title="Sync with Salesforce"
        bodyStyle={{overflow: 'visible'}}
        contentStyle={{top: dialogTopOffset, width: '500px'}}>
        <ThemeProvider theme={lightTheme}>
          <p>
            Use the fields below to find the contact or lead that this call is associated with.
            Start typing in either field to begin a search.
          </p>
          <img
            alt="dialog close"
            className="dialog-close-button"
            src={CloseIcon}
            onClick={this.onTouchTapCloseButton}
          />
          <div className="form-container">
            <form>
              {this.renderContactLeadTipLine()}
              {this.renderMissingFulfillDataTipLine()}
              <label className="form-label-bold form-label">Contact:</label>
              <CallSyncCRMContactLeadSearchInputBoxContainer
                id="contact"
                placeholder="Enter a Lead or Contact"
              />
              <label className="form-label-bold form-label">Related to:</label>
              <CallSyncCRMRelatedToSearchInputBoxContainer
                id="relatedTo"
                placeholder="Choose a Related Record"
                disabledPlaceholder="N/A - Unavailable When a Lead is Selected"
              />
              <div style={{marginTop: '30px'}}>
                <Button
                  variant="contained"
                  color="primary"
                  className="ev-button"
                  disabled={
                    this.dialogDataFulfillType() === CallSyncCRMDataFulfillType.MISSING_RELATED_TO
                  }
                  style={{width: '120px'}}
                  onClick={this.onTouchTapSaveButton}>
                  Save
                </Button>
                <Button
                  variant="outlined"
                  className="ev-button"
                  onClick={this.onTouchTapCloseButton}
                  style={{width: '120px', marginLeft: '10px'}}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </ThemeProvider>
      </Dialog>
    );
  }
}

export default CallSyncCRMDialog;
